import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { orderToPrintService } from '../infra';
import { useOrderInvoicePrintingLang } from './useOrderInvoicePrintingLang';

import { turboHelper } from 'business/order/geoqueue';
import {
  orderPrintingConfigAnalytic,
  orderPrintingConfigService,
} from 'components/settings-options-and-extensions/components/order-printing-config/infra';
import lsService from 'services/local-storage-service';
import ordersUtils from 'utils/OrdersUtils';
import { orderToPrintAnalytic } from '../infra/analytics';

import type { PartnerId } from 'shared/domain';
import type { OrderIdForcedProps } from '../domain';

export function useOrderInvoicePrinting(props: OrderIdForcedProps) {
  const { orderIdForced = NaN } = props;

  const localeLang = useSelector<any, string>(s => s.appReducer.language);
  const partnerId = useSelector<any, PartnerId>(
    s => s.partnerReducer.profile.id,
  );

  const {
    isRenderForced,
    order,
    orderId,
    hasOrderToPrint,
    confirmOrderPrinting,
    calcTotalUnitsOfOrder,
  } = orderToPrintService({ orderIdForced });
  const {
    validateWhichPrintedEnabled,
    validateAttemptsToEnablePrinting,
  } = orderPrintingConfigService();

  const printedEnabled = validateWhichPrintedEnabled();
  const deliveryMethod = order.deliveryMethod;
  const isPickup = ordersUtils.isPickup(deliveryMethod);
  const isTurbo = turboHelper.isTurboqueue(order.specialOrder);
  const isMarketplace = ordersUtils.isMarketPlace(deliveryMethod);

  const hide = {
    clientTag: !Boolean(order.user.type),
    isPickup: !isPickup,
    contact: {
      phone: !(isMarketplace && Boolean(order.user.contact.phone)),
      address: !(
        isMarketplace &&
        (Boolean(order.user.contact.address.base) ||
          Boolean(order.user.contact.address.description))
      ),
      zipCode: !(isMarketplace && Boolean(order.user.contact.zipCode)),
      neighborhood: !(
        isMarketplace && Boolean(order.user.contact.neighborhood)
      ),
    },
    paymentMethod: !(
      ordersUtils.isMarketPlace(deliveryMethod) ||
      (lsService.isChile() && ordersUtils.isEdenred(deliveryMethod))
    ),
    shippingPrime: !(isMarketplace && Boolean(order.primeShippingDiscount)),
    commandBase: !(printedEnabled === 'all' || printedEnabled === 'comanda'),
    totals: {
      error: order.totals.error === null,
      breakdown: order.totals.error !== null,
      notes: {
        aside: false,
        cash: {
          receivedFromUser: !(
            isMarketplace && !Number.isNaN(order.cashReceivedFromUser)
          ),
          deliveredToUser: !(
            isMarketplace &&
            order.cashReceivedFromUser > order.totals.main.price
          ),
        },
      },
    },
    handshake: {
      section: false,
      qrCode: !Boolean(order.handshake.qrCode),
      manualCode: !Boolean(order.handshake.manualCode),
    },
    checklistSection: !(
      printedEnabled === 'all' || printedEnabled === 'checklist'
    ),
  };
  hide.handshake.section = hide.handshake.qrCode && hide.handshake.manualCode;
  hide.totals.notes.aside =
    hide.totals.notes.cash.receivedFromUser &&
    hide.totals.notes.cash.deliveredToUser;

  const lang = useOrderInvoicePrintingLang({
    orderId,
    storeName: order.store.name,
    contact: order.user.contact,
    paymentMethod: order.paymentMethod,
    numberOfProducts: calcTotalUnitsOfOrder(order.products),
    totalsErrorMsg: order.totals.error?.message ?? '',
    cashReceivedFromUser: order.cashReceivedFromUser,
    cashDeliveredToUser: order.cashReceivedFromUser - order.totals.main.price,
    manualCode: order.handshake.manualCode,
    userType: order.user.type,
  });

  useEffect(() => {
    if (validateAttemptsToEnablePrinting()) {
      orderPrintingConfigAnalytic.logPrintSetup({
        partnerId,
        command: true,
        checklist: true,
        autoPrint: false,
      });
    }
  }, []);

  useEffect(() => {
    if (hasOrderToPrint) {
      confirmOrderPrinting(order.id);
      orderToPrintAnalytic.logPrintedOrder({
        partnerId,
        printedEnabled,
        orderId: order.id,
        storeId: order.store.id,
        orderCreatedAt: order.createdAt,
        hasQRCode: Boolean(order.handshake.qrCode),
      });
    }
  }, [hasOrderToPrint]);

  return {
    isRenderForced,
    lang,
    localeLang,
    order,
    orderId,
    totals: order.totals,
    isTurbo,
    hide,
  };
}
